/* eslint-disable @next/next/no-img-element */
import { WeeklyTierListRank } from "@dimensional-engineering/dimensional-models";

import styles from "./WeeklyTierList.module.css";

import SmallProfileImage from "@/components/shared/SmallProfileImage/SmallProfileImage";
import { RootState } from "@/redux/store";

export type TierPeerPoint = {
  authorUid: string;
  rank: WeeklyTierListRank;
  imageUrl: string;
  userName: string;
  publicProfile: RootState["connections"]["publicProfiles"][number];
};

export const TIER_ROWS: WeeklyTierListRank[] = [
  WeeklyTierListRank.s,
  WeeklyTierListRank.a,
  WeeklyTierListRank.b,
  WeeklyTierListRank.c,
  WeeklyTierListRank.d,
];

export const TIER_COLORS: Record<WeeklyTierListRank, string> = {
  [WeeklyTierListRank.s]: "#EF453C", // passion
  [WeeklyTierListRank.a]: "#F37A1C", // energy
  [WeeklyTierListRank.b]: "#E0CC65", // warmth
  [WeeklyTierListRank.c]: "#5DAF4D", // openness
  [WeeklyTierListRank.d]: "#6AB4EA", // tranquility
};

/**
 * Vertical stack of tier blocks (S/A/B/C/D).
 *
 * Two variants:
 *  - `rows` (default): each tier row has a flex strip to its right where the
 *    user's avatar and peer avatars are placed inline. Used by the modal.
 *  - `spine`: tier letters on the left, a stylized vertical bracket spine
 *    with a stub pointing right at the user's rank, and a single avatar
 *    aligned to that stub. Used by the dashboard CTA.
 *
 * The share view uses the `rows` variant with `showArrow`.
 */
export function WeeklyTierListTiers(props: {
  myRank: WeeklyTierListRank | null;
  myImageUrl: string;
  myPublicProfile: RootState["user"]["publicProfileData"];
  peerPoints: TierPeerPoint[];
  showMyPoint?: boolean;
  highlightedRank?: WeeklyTierListRank | null;
  onMyPointClick?: () => void;
  onPeerPointClick?: (point: TierPeerPoint) => void;
  showArrow?: boolean;
  variant?: "rows" | "spine";
  compact?: boolean;
}) {
  const showMyPoint = (props.showMyPoint ?? true) && !!props.myRank;
  const highlighted = props.highlightedRank ?? props.myRank;
  const variant = props.variant ?? "rows";

  if (variant === "spine") {
    const myRankIndex = props.myRank ? TIER_ROWS.indexOf(props.myRank) : -1;
    // Tier blocks are 50px tall with 6px gaps → row pitch = 56px.
    // Middle of row N (for the bracket stub): N*56 + 25
    // Top of row N (for the avatar / rank label): N*56
    const stubTop = myRankIndex >= 0 ? myRankIndex * 56 + 25 : -1;
    const avatarTop = myRankIndex >= 0 ? myRankIndex * 56 : -1;
    const columnDimmed = !showMyPoint;

    return (
      <div className={styles.spineWrap}>
        <div
          className={`${styles.spineColumn} ${
            columnDimmed ? styles.spineColumnDim : ""
          }`}
        >
          {TIER_ROWS.map((rank) => {
            return (
              <div
                className={styles.spineBlock}
                key={rank}
                style={{ backgroundColor: TIER_COLORS[rank] }}
              >
                <span className={styles.tierLetter}>{rank}</span>
              </div>
            );
          })}
        </div>

        <div className={styles.spineBracket} aria-hidden="true">
          <div className={styles.spineBracketLine} />
          <div className={styles.spineBracketTopHook} />
          <div className={styles.spineBracketBottomHook} />
          {showMyPoint && stubTop >= 0 && (
            <div
              className={styles.spineBracketStub}
              style={{ top: `${stubTop}px` }}
            />
          )}
        </div>

        {showMyPoint && avatarTop >= 0 && (
          <>
            <div
              className={styles.spineAvatar}
              style={{ top: `${avatarTop}px` }}
            >
              <SmallProfileImage
                noLink
                medium
                imageUrl={props.myImageUrl || "/default-image.png"}
                publicProfile={props.myPublicProfile}
              />
            </div>
            <p
              className={styles.spineRankLabel}
              style={{ top: `${avatarTop + 17}px` }}
            >
              Your rank: {props.myRank}
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={styles.tiersWrap}>
      {TIER_ROWS.map((rank) => {
        const peersForRank = props.peerPoints.filter((p) => p.rank === rank);
        const isHighlighted = highlighted === rank;
        const isMyRank = props.myRank === rank;
        const dimmed = highlighted !== null && highlighted !== undefined && !isHighlighted;

        return (
          <div className={styles.tierRow} key={rank}>
            <div
              className={`${styles.tierBlock} ${dimmed ? styles.tierBlockDim : ""}`}
              style={{ backgroundColor: TIER_COLORS[rank] }}
            >
              <span className={styles.tierLetter}>{rank}</span>
            </div>

            {props.showArrow && isMyRank && showMyPoint && (
              <div className={styles.tierArrow} aria-hidden="true">
                →
              </div>
            )}

            <div className={styles.tierAvatars}>
              {isMyRank && showMyPoint && (
                <div
                  className={styles.tierAvatarPin}
                  onClick={(event) => {
                    if (!props.onMyPointClick) {
                      return;
                    }
                    event.stopPropagation();
                    props.onMyPointClick();
                  }}
                >
                  <SmallProfileImage
                    noLink
                    medium
                    imageUrl={props.myImageUrl || "/default-image.png"}
                    publicProfile={props.myPublicProfile}
                  />
                </div>
              )}
              {peersForRank.map((point) => (
                <div
                  key={point.authorUid}
                  className={styles.tierAvatarPin}
                  onClick={(event) => {
                    if (!props.onPeerPointClick) {
                      return;
                    }
                    event.stopPropagation();
                    props.onPeerPointClick(point);
                  }}
                >
                  <SmallProfileImage
                    noLink
                    medium
                    imageUrl={point.imageUrl}
                    publicProfile={point.publicProfile}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
